import { Portal, Root as ModalRoot } from '@radix-ui/react-dialog';
import { cloneElement, useCallback, useEffect, useRef } from 'react';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';

import { DEFAULT_PANEL_WIDTH, OVERLAY_CLASS, PANEL_CLASS } from './constants';
import { SlideOutOverlay } from './SlideOutOverlay';
import { SlideOutPanel } from './SlideOutPanel';
import type { SlideOutProps } from './types';

export function SlideOut({
  children,
  panelWidth = DEFAULT_PANEL_WIDTH,
  dangerouslyOverrideClassName,
  onOpenChange,
  open,
  ...delegatedProps
}: ComponentProps<SlideOutProps>) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelClassName = getClassNameOverride(
    { dangerouslyOverrideClassName },
    'SlideOut',
  );

  const closeSlideOut = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleClose = useCallback(() => {
    overlayRef.current?.classList.remove(OVERLAY_CLASS.show);
    panelRef.current?.classList.remove(PANEL_CLASS.show);

    overlayRef.current?.addEventListener('animationend', closeSlideOut, {
      once: true,
    });

    requestAnimationFrame(() => {
      // Animate the overlay and panel out of view as they close.
      overlayRef.current?.classList.add(OVERLAY_CLASS.hide);

      if (panelRef.current) {
        panelRef.current.classList.add(PANEL_CLASS.disable);
        panelRef.current.style.transform = `translateX(${panelWidth})`;
      }
    });
  }, [overlayRef, panelRef, panelWidth, closeSlideOut]);

  useEffect(() => {
    const overlay = overlayRef.current;

    return () => {
      overlay?.removeEventListener('animationend', closeSlideOut);
    };
  }, [overlayRef, closeSlideOut]);

  if (!open) {
    return null;
  }

  // Attaches additional props to the Panel component that is passed in.
  const panelContent = cloneElement(children, {
    onClose: handleClose,
    ref: panelRef,
  });

  return (
    <ModalRoot
      open={open}
      onOpenChange={onOpenChange}
      {...delegatedProps}
      modal={true}
    >
      <Portal>
        <div className="fixed inset-0 z-30" role="dialog" aria-modal="true">
          <SlideOutOverlay overlayRef={overlayRef} onClose={handleClose} />
          <SlideOutPanel
            panelRef={panelRef}
            panelWidth={panelWidth}
            className={panelClassName}
          >
            {panelContent}
          </SlideOutPanel>
        </div>
      </Portal>
    </ModalRoot>
  );
}

SlideOut.displayName = 'SlideOut';
